import React, { useMemo, useState } from 'react';
import { useBalancesPageQuery } from '../../../../services';
import {
    COwnAccountsBalances,
    CTableWrapper,
    CVerticalDropdown,
    DBlock,
    DPage,
    DPageBody,
    DPageLoader,
    ICustomTableWrapperProps,
} from '../../../dum';
import {
    copyToClipboard,
    formatFloat,
    formatLongString,
    getDateFromString,
    round,
    useBoolean,
} from '../../../../helpers';
import { useAppDispatch } from '../../../../app/hooks';
import { pushMessage } from '../../../../features/global-messages/global-messages-slice';
import dayjs, { Dayjs } from 'dayjs';
import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    EditNoteOutlined,
    InsertDriveFileOutlined,
} from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import {
    SUpdateOrderSaleDetailsDialog,
    SUpdateExpenseDetailsDialog,
    SUpsertOrderSale,
    SUpsertExpenseDialog,
} from '../../../smart';
import { SxProps } from '@mui/material';

interface PendingItem {
    id?: number | null;
    account_name: string;
    expected_payment_date: Dayjs | null;
    date: Dayjs | null;
    order_code: string;
    type?: 'sale' | 'expense';
    cumulative_amount: number;
    pre_cumulative_amount: number;
    to_be_charged: number;
    to_be_paid: number;
    notes: string;
    // Expense rows only: drafts are indicated in both balance views and edited
    // only from the full expense form.
    is_draft?: boolean;
    resources?: string;
    total_groups?: number;
    total_kilos?: number;
    is_total?: boolean;
    // Colored spacer row between the vencidos / hoy / futuros / sin-fecha
    // groups.
    is_separator?: boolean;
}

function filterByExpectedDate({
    expected_payment_date: expectedPaymentDateDayjs,
    today: todayDayJs,
    include_after: includeAfter,
}: {
    expected_payment_date: Dayjs;
    today: Dayjs;
    include_after: boolean;
}): boolean {
    if (includeAfter) {
        return true;
    }
    const isExpectedAfter = expectedPaymentDateDayjs
        .startOf('month')
        .isAfter(todayDayJs.startOf('month'));
    return isExpectedAfter ? false : true;
}

// Human phrasing of a calendar-day distance from today: "hoy", "ayer",
// "pasado mañana", "en una semana", "hace 2 meses"... Hand-rolled instead of
// dayjs .from() because dayjs's default thresholds never produce weeks.
function formatRelativeToToday(target: Dayjs, today: Dayjs): string {
    const targetDay = dayjs(target.format('YYYY-MM-DD'));
    const todayDay = dayjs(today.format('YYYY-MM-DD'));
    const days = targetDay.diff(todayDay, 'day');
    if (days === 0) return 'hoy';
    if (days === 1) return 'mañana';
    if (days === 2) return 'pasado mañana';
    if (days === -1) return 'ayer';
    if (days === -2) return 'anteayer';

    const abs = Math.abs(days);
    let quantity: string;
    if (abs < 7) {
        quantity = `${abs} días`;
    } else if (abs < 30) {
        const weeks = Math.round(abs / 7);
        quantity = weeks === 1 ? 'una semana' : `${weeks} semanas`;
    } else if (abs < 365) {
        const months = Math.round(abs / 30);
        quantity = months === 1 ? 'un mes' : `${months} meses`;
    } else {
        const years = Math.round(abs / 365);
        quantity = years === 1 ? 'un año' : `${years} años`;
    }
    return days > 0 ? `en ${quantity}` : `hace ${quantity}`;
}

// Folio cell: click to copy the folio to the clipboard. stopPropagation keeps
// the click from also opening the row's document dialog.
function FolioCell({ folio }: { folio: string }) {
    const dispatch = useAppDispatch();

    if (!folio) return null;

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const ok = await copyToClipboard(folio);
        dispatch(
            pushMessage({
                message: ok ? `Folio ${folio} copiado` : 'No se pudo copiar',
                options: { variant: ok ? 'success' : 'error' },
            }),
        );
    };

    return (
        <Tooltip arrow title={'Copiar folio'}>
            <Box
                component={'span'}
                data-item-single-click
                onClick={handleCopy}
                sx={{
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                {folio}
            </Box>
        </Tooltip>
    );
}

export function BalancesPage() {
    const { data, loading } = useBalancesPageQuery();
    const [openSaleDetails, setOpenSaleDetails] = useState(false);
    const [openSale, setOpenSale] = useState(false);
    const [openExpense, setOpenExpense] = useState(false);
    const [openExpenseDetails, setOpenExpenseDetails] = useState(false);
    const [pendingItemSelected, setPendingItemSelected] =
        useState<PendingItem | null>(null);
    const [view, setView] = useBoolean({
        KEY: BalancesPage.name + '_view',
        defaultValue: true,
    });

    const [includeNextMonthsSales, setIncludeNextMonthSales] = useBoolean({
        KEY: BalancesPage.name + '_IncludeNextMonthSales',
        defaultValue: false,
    });
    const [includeNextMonthExpenses, setIncludeNextMonthExpenses] = useBoolean({
        KEY: BalancesPage.name + '_IncludeNextMonthExpenses',
        defaultValue: true,
    });

    const pendingItems: PendingItem[] = useMemo(() => {
        const items: PendingItem[] = [];

        const todayDayjs = dayjs().utc();

        // Formal (fiscal) receipt types = the non-informal ones. Replaces the
        // hardcoded receipt_type_id === 2 / === 1 split — the "fiscal" view shows
        // formal types, the other view shows the informal (nota) rest.
        const accountabilityReceiptTypeIds = new Set(
            (data?.getReceiptTypes ?? [])
                .filter((rt) => !rt.is_informal_receipt)
                .map((rt) => rt.id),
        );
        const isAccountabilityReceipt = (
            receiptTypeId: number | null | undefined,
        ) =>
            receiptTypeId !== null &&
            receiptTypeId !== undefined &&
            accountabilityReceiptTypeIds.has(receiptTypeId);

        if (data) {
            items.push(
                ...data.getExpensesWithDisparities
                    .filter((ex) =>
                        view
                            ? isAccountabilityReceipt(ex.receipt_type_id)
                            : !isAccountabilityReceipt(ex.receipt_type_id),
                    )
                    .filter((ex) => {
                        if (!ex.expected_payment_date) {
                            return true;
                        }
                        return filterByExpectedDate({
                            expected_payment_date: dayjs(
                                ex.expected_payment_date,
                            ).utc(),
                            today: todayDayjs,
                            include_after: includeNextMonthExpenses,
                        });
                    })
                    .map((ex) => {
                        return {
                            id: ex.id,
                            account_name: ex.account?.name || '',
                            // Negative when the supplier was overpaid.
                            to_be_charged: round(
                                ex.total_with_tax - ex.transfer_receipts_total,
                            ),
                            to_be_paid: 0,
                            expected_payment_date: ex.expected_payment_date
                                ? dayjs(
                                      getDateFromString(
                                          ex.expected_payment_date,
                                      ),
                                  ).utc()
                                : null,
                            order_code: ex.external_code,
                            type: 'expense' as PendingItem['type'],
                            cumulative_amount: 0,
                            pre_cumulative_amount: 0,
                            notes: ex.notes,
                            is_draft: ex.is_draft,
                            date: ex.date ? dayjs(ex.date).utc() : null,
                            resources: ex.expense_resources
                                .map((er) => er.resource?.name)
                                .filter(Boolean)
                                .join(', '),
                        };
                    }),
            );

            items.push(
                ...data.getOrderSalesWithDisparities
                    .filter((os) =>
                        view
                            ? isAccountabilityReceipt(os.receipt_type_id)
                            : !isAccountabilityReceipt(os.receipt_type_id),
                    )
                    .filter((os) => {
                        if (!os.expected_payment_date) {
                            return true;
                        }
                        return filterByExpectedDate({
                            expected_payment_date: dayjs(
                                os.expected_payment_date,
                            ).utc(),
                            today: todayDayjs,
                            include_after: includeNextMonthsSales,
                        });
                    })
                    .map((os) => {
                        return {
                            id: os.id,
                            account_name: os.account?.name || '',
                            // Net of Devolución adjustments; negative when the
                            // client overpaid.
                            to_be_paid: round(
                                os.net_total_with_tax -
                                    os.transfer_receipts_total,
                            ),
                            expected_payment_date: os.expected_payment_date
                                ? dayjs(
                                      getDateFromString(
                                          os.expected_payment_date,
                                      ),
                                  ).utc()
                                : null,
                            order_code: isAccountabilityReceipt(
                                os.receipt_type_id,
                            )
                                ? String(os.invoice_code)
                                : String(os.order_code),
                            to_be_charged: 0,
                            type: 'sale' as PendingItem['type'],
                            cumulative_amount: 0,
                            pre_cumulative_amount: 0,
                            notes: os.notes,
                            date: os.date ? dayjs(os.date).utc() : null,
                            total_groups: os.order_sale_products.reduce(
                                (acc, p) => acc + (p.groups || 0),
                                0,
                            ),
                            total_kilos: os.order_sale_products.reduce(
                                (acc, p) => acc + (p.kilos || 0),
                                0,
                            ),
                        };
                    }),
            );

            items.sort((a, b) => {
                if (
                    a.expected_payment_date === null &&
                    b.expected_payment_date === null
                ) {
                    if (a.type === 'sale' && b.type === 'expense') {
                        return 1;
                    } else if (a.type === 'expense' && b.type === 'sale') {
                        return -1;
                    } else {
                        if (a.account_name < b.account_name) {
                            return -1;
                        } else if (a.account_name > b.account_name) {
                            return 1;
                        } else {
                            if (a.date && b.date) {
                                return a.date.isBefore(b.date)
                                    ? -1
                                    : a.date.isAfter(b.date)
                                    ? 1
                                    : 0;
                            } else if (a.date) {
                                return -1;
                            } else {
                                return 1;
                            }
                        }
                    }
                } else if (a.expected_payment_date === null) {
                    return 1;
                } else if (b.expected_payment_date === null) {
                    return -1;
                } else {
                    return a.expected_payment_date.isBefore(
                        b.expected_payment_date,
                    )
                        ? -1
                        : a.expected_payment_date.isAfter(
                              b.expected_payment_date,
                          )
                        ? 1
                        : a.account_name.localeCompare(b.account_name);
                }
            });

            const initialCumulativeAmount: number =
                data.getOwnAccountsTransferSummary
                    .filter((account) => {
                        const excluded =
                            !!account.account?.is_informal_account;
                        return view ? !excluded : excluded;
                    })
                    .reduce((acc, curr) => {
                        return acc + (curr.current_amount || 0);
                    }, 0);
            let toBeCharged: number = 0;
            let toBePaid: number = 0;

            let cumulativeAmount = initialCumulativeAmount;

            items.forEach((item) => {
                item.pre_cumulative_amount = cumulativeAmount;
                cumulativeAmount =
                    cumulativeAmount + item.to_be_paid - item.to_be_charged;
                item.cumulative_amount = cumulativeAmount;
                toBePaid += item.type === 'sale' ? item.to_be_paid : 0;
                toBeCharged += item.type === 'expense' ? item.to_be_charged : 0;
            });

            items.push({
                id: null,
                account_name: '',
                to_be_paid: toBePaid,
                to_be_charged: toBeCharged,
                expected_payment_date: null,
                order_code: '',
                cumulative_amount: cumulativeAmount,
                pre_cumulative_amount: initialCumulativeAmount,
                notes: '',
                date: null,
                is_total: true,
            });
        }

        return items;
    }, [data, view, includeNextMonthsSales, includeNextMonthExpenses]);

    const today = dayjs();
    const totalRow = pendingItems.find((item) => item.is_total);
    const dataItems = pendingItems.filter((item) => !item.is_total);

    // Calendar-day distance from today; null = no expected payment date.
    const daysFromToday = (item: PendingItem): number | null =>
        item.expected_payment_date
            ? dayjs(item.expected_payment_date.format('YYYY-MM-DD')).diff(
                  dayjs(today.format('YYYY-MM-DD')),
                  'day',
              )
            : null;

    // One merged table in four groups — vencidos (1+ días atrás), hoy,
    // futuros (1+ días adelante), sin fecha — with a blank separator row
    // between non-empty neighbors. Items keep the global (cumulative) order
    // computed above; the slices don't re-sort.
    const groups: PendingItem[][] = [
        dataItems.filter((item) => {
            const days = daysFromToday(item);
            return days !== null && days < 0;
        }),
        dataItems.filter((item) => daysFromToday(item) === 0),
        dataItems.filter((item) => {
            const days = daysFromToday(item);
            return days !== null && days > 0;
        }),
        dataItems.filter((item) => !item.expected_payment_date),
    ];

    const makeSeparator = (): PendingItem => ({
        id: null,
        account_name: '',
        expected_payment_date: null,
        date: null,
        order_code: '',
        cumulative_amount: 0,
        pre_cumulative_amount: 0,
        to_be_charged: 0,
        to_be_paid: 0,
        notes: '',
        is_separator: true,
    });

    const tableData: PendingItem[] = [];
    groups.forEach((group) => {
        if (group.length === 0) {
            return;
        }
        if (tableData.length > 0) {
            tableData.push(makeSeparator());
        }
        tableData.push(...group);
    });
    // The total row reflects the global cumulative value — always last.
    if (totalRow) {
        tableData.push(totalRow);
    }

    // Double-clicking a row (or the context "Editar") opens the FULL upsert —
    // the sale upsert for sales, the expense upsert for expenses. Each enforces
    // its own status-1 edit lock. The single-click icon is the lighter
    // optional-details edit instead.
    const handleOpenItem = (item: PendingItem) => {
        if (item.type === 'sale') {
            setPendingItemSelected(item);
            setOpenSale(true);
        } else if (item.type === 'expense') {
            setOpenExpense(true);
            setPendingItemSelected(item);
        }
    };

    // Optional/side-effect-free fields (expected payment date,
    // supplement, credit note, canceled) through the lightweight details
    // endpoint, which is not subject to the status-1 edit lock.
    const handleOpenSaleDetails = (item: PendingItem) => {
        if (item.type !== 'sale') {
            return;
        }
        setPendingItemSelected(item);
        setOpenSaleDetails(true);
    };

    // Expense counterpart of handleOpenSaleDetails — the optional-details edit
    // (folio, payment date, supplement, conciliation, canceled).
    const handleOpenExpenseDetails = (item: PendingItem) => {
        if (item.type !== 'expense') {
            return;
        }
        setPendingItemSelected(item);
        setOpenExpenseDetails(true);
    };

    const tableColumns: ICustomTableWrapperProps<PendingItem>['items'] = [
        {
            title: 'Folio',
            content: (pi) => {
                return <FolioCell folio={pi.order_code} />;
            },
        },
        {
            title: 'Cliente/Proveedor',
            cellGrow: 3,
            textAlign: 'left',
            content: (pi) => {
                return formatLongString(pi.account_name);
            },
        },
        {
            title: 'Fecha de la factura',
            textAlign: 'left',
            content: (pi) => {
                // Document date (sale/expense date), not the expected payment
                // date — that one only drives the Plazo column and the sort.
                if (pi.date === null) {
                    return '';
                }
                // Global dayjs locale is 'es' → "vie. 15 may."; strip the
                // abbreviation periods to get "vie 15 may".
                return pi.date.format('ddd D MMM').replace(/\./g, '');
            },
            cellSx: () => {
                return {
                    whiteSpace: 'nowrap',
                };
            },
        },
        {
            title: 'Plazo',
            textAlign: 'left',
            content: (pi) => {
                if (pi.is_total) {
                    return null;
                }
                // Only meaningful for rows with an expected payment date;
                // unassigned rows stay blank.
                const reference = pi.expected_payment_date;
                if (!reference) {
                    return '';
                }
                // Absolute date in the same format as the Fecha column, with
                // the relative phrase ("en 3 días", "hace 2 meses") in parens.
                return (
                    <Typography variant={'caption'}>
                        {reference.format('ddd D MMM').replace(/\./g, '')} (
                        {formatRelativeToToday(reference, today)})
                    </Typography>
                );
            },
            cellSx: () => {
                return {
                    whiteSpace: 'nowrap',
                };
            },
        },
        {
            title: 'Ingreso',
            cellGrow: 1,
            textAlign: 'right',
            content: (pi) => {
                if (pi.is_separator) {
                    return null;
                }
                // Money coming in = sale items (to_be_paid). Expense rows leave
                // this blank; the total row shows the summed sales.
                if (!pi.is_total && pi.type !== 'sale') {
                    return '';
                }
                return (
                    <Typography
                        component={'span'}
                        variant={'body2'}
                        sx={{ fontWeight: pi.is_total ? 700 : 400 }}
                    >
                        {formatFloat(pi.to_be_paid)}
                    </Typography>
                );
            },
        },
        {
            title: 'Egreso',
            cellGrow: 1,
            textAlign: 'right',
            content: (pi) => {
                if (pi.is_separator) {
                    return null;
                }
                // Money going out = expense items (to_be_charged). Sale rows
                // leave this blank; the total row shows the summed expenses.
                if (!pi.is_total && pi.type !== 'expense') {
                    return '';
                }
                return (
                    <Typography
                        component={'span'}
                        variant={'body2'}
                        sx={{ fontWeight: pi.is_total ? 700 : 400 }}
                    >
                        {formatFloat(pi.to_be_charged)}
                    </Typography>
                );
            },
        },
        {
            title: 'Acumulado',
            cellGrow: 1,
            textAlign: 'right',
            content: (pi) => {
                if (pi.is_separator) {
                    return null;
                }
                // Running cumulative balance, neutral color (the Ingreso /
                // Egreso split already conveys the direction of each movement).
                return (
                    <Typography
                        component={'span'}
                        variant={'body2'}
                        sx={{ fontWeight: pi.is_total ? 700 : 400 }}
                    >
                        {formatFloat(pi.cumulative_amount)}
                    </Typography>
                );
            },
        },
        {
            // Notes are now scanned directly in this column instead of on a
            // hover tooltip. Fixed width (equal min/max) with wrapping so the
            // column never resizes with the length of a row's notes. The content
            // carries its own left padding to separate it from the right-aligned
            // Acumulado column next to it (the global `& td` padding rule would
            // override a cell-level pl, so indent the content instead).
            title: 'Notas',
            textAlign: 'left',
            content: (pi) => {
                if (pi.is_total || pi.is_separator || !pi.notes) {
                    return null;
                }
                return <Box sx={{ pl: 2 }}>{pi.notes}</Box>;
            },
            cellSx: () => ({
                width: 240,
                minWidth: 240,
                maxWidth: 240,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
            }),
        },
        {
            // Draft indicator only: the warning icon with no tooltip. Notes live
            // in their own column now.
            title: ' ',
            // Status and actions use matching widths and right-aligned icons.
            cellGrow: 0.5,
            textAlign: 'right',
            content: (item) => {
                if (item.is_total || item.is_separator) {
                    return null;
                }
                if (item.type === 'expense' && item.is_draft) {
                    return (
                        <InsertDriveFileOutlined
                            fontSize={'small'}
                            color={'warning'}
                        />
                    );
                }
                return null;
            },
        },
        {
            title: ' ',
            cellGrow: 0.5,
            textAlign: 'right',
            content: (item) => {
                if (item.is_total || item.is_separator) {
                    return null;
                }
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                        }}
                    >
                        {/* Single-click edit = the optional/side-effect-free
                            fields only (both sales and expenses). Restructuring
                            the record itself is the full upsert, reached by
                            double-clicking the row. */}
                        <Tooltip arrow title={'Editar datos opcionales'}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.type === 'sale') {
                                        handleOpenSaleDetails(item);
                                    } else {
                                        handleOpenExpenseDetails(item);
                                    }
                                }}
                            >
                                <EditNoteOutlined />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    // Group boundaries are marked by colored separator rows: black on the
    // dark theme, grey on the light one. The backgroundColor here wins over
    // the stripe because DTableBodyRow spreads rowSx last.
    const rowSx = (item: PendingItem): SxProps<Theme> => {
        if (item.is_separator) {
            return {
                height: 16,
                backgroundColor: (theme: Theme) =>
                    theme.palette.mode === 'dark'
                        ? theme.palette.common.black
                        : grey['400'],
                '& td': { borderBottom: 0, py: 0 },
            };
        }
        return {};
    };

    return (
        <DPage>
            <DPageBody disableGutters>
                <DBlock>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Always reserves the left flex space so the vertical
                            dropdown stays pinned right — COwnAccountsBalances
                            renders null when there are no non-zero accounts
                            (including while loading), which would otherwise let
                            space-between pull the dropdown to the left. */}
                        <Box sx={{ flex: 1, minWidth: 0, mb: 2, ml: 1 }}>
                            <COwnAccountsBalances
                                loading={loading}
                                accounts={(
                                    data?.getOwnAccountsTransferSummary ?? []
                                ).filter((account) => {
                                    const excluded =
                                        !!account.account?.is_informal_account;
                                    return view ? !excluded : excluded;
                                })}
                            />
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            <CVerticalDropdown
                                label={'Acciones'}
                                items={[
                                    {
                                        component: (
                                            <Switch
                                                onChange={() => {
                                                    setView(!view);
                                                }}
                                                value={view}
                                            />
                                        ),
                                    },
                                    {
                                        component: (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={includeNextMonthsSales}
                                                        onChange={() => setIncludeNextMonthSales(!includeNextMonthsSales)}
                                                        size="small"
                                                    />
                                                }
                                                label={'Incluir ventas de los proximos meses'}
                                            />
                                        ),
                                    },
                                    {
                                        component: (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={includeNextMonthExpenses}
                                                        onChange={() => setIncludeNextMonthExpenses(!includeNextMonthExpenses)}
                                                        size="small"
                                                    />
                                                }
                                                label={'Incluir gastos de los proximos meses'}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 2 }} />
                    {loading ? (
                        <DPageLoader />
                    ) : (
                        <Box sx={{ flex: 1, minHeight: 0, '& .MuiTableContainer-root': { height: '100%' } }}>
                            <CTableWrapper
                                stickyHeader
                                centerHeaders
                                onRowDoubleClick={handleOpenItem}
                                padding={false}
                                contextItems={[
                                    {
                                        title: 'Editar',
                                        callback: handleOpenItem,
                                        disable: (item) =>
                                            !!item.is_total || !!item.is_separator,
                                    },
                                ]}
                                items={tableColumns}
                                rowSx={rowSx}
                                data={tableData}
                            />
                        </Box>
                    )}
                </DBlock>
            </DPageBody>
            <SUpdateOrderSaleDetailsDialog
                open={openSaleDetails}
                setOpen={setOpenSaleDetails}
                orderSaleId={pendingItemSelected?.id ?? null}
            />
            <SUpsertOrderSale
                open={openSale}
                setOpen={setOpenSale}
                orderSaleId={pendingItemSelected?.id ?? null}
            />
            <SUpdateExpenseDetailsDialog
                open={openExpenseDetails}
                setOpen={setOpenExpenseDetails}
                expenseId={pendingItemSelected?.id ?? null}
            />
            <SUpsertExpenseDialog
                open={openExpense}
                setOpen={setOpenExpense}
                expenseId={pendingItemSelected?.id}
            />
        </DPage>
    );
}
